import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Rocket, CheckCircle2, AlertTriangle, XCircle, MinusCircle, ShieldAlert, ChevronRight, Loader2 } from "lucide-react";

export function GoLiveReadinessPanel({ cid, onChange, onGoStep }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPassed, setShowPassed] = useState(false);

  const load = () => api.get(`/platform/companies/${cid}/readiness`).then((x) => setR(x.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); if (msg) toast.success(msg); load(); onChange && onChange(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const setStatus = (status, label) => act(() => api.patch(`/platform/companies/${cid}/onboarding`, { onboarding_status: status }), label);
  const override = (key) => { const reason = window.prompt("Reason for overriding this check?"); if (reason && reason.trim()) act(() => api.post(`/platform/companies/${cid}/readiness/override`, { check_key: key, reason: reason.trim() }), "Override added"); };
  const unoverride = (key) => act(() => api.delete(`/platform/companies/${cid}/readiness/override/${key}`), "Override removed");

  if (!r) return <div data-testid="step-panel-go_live" className="text-sm text-slate-400">Loading…</div>;

  const Row = ({ c, tone }) => (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50" data-testid={`readiness-check-${c.key}`}>
      <span className="min-w-0 flex-1">
        <span className="text-sm text-slate-800">{c.label}</span>
        {c.detail && <span className="block text-[11px] text-slate-400 truncate">{c.detail}</span>}
        {c.overridden && <span className="block text-[11px] text-amber-600">Overridden by {c.override?.actor}: {c.override?.reason}</span>}
      </span>
      {c.fix_step && !c.passed && !c.overridden && (
        <button onClick={() => onGoStep && onGoStep(c.fix_step)} data-testid={`readiness-fix-${c.key}`} className="inline-flex items-center gap-0.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700">Fix <ChevronRight className="w-3 h-3" /></button>
      )}
      {tone === "blocker" && c.overrideable && !c.overridden && (
        <button onClick={() => override(c.key)} disabled={busy} data-testid={`readiness-override-${c.key}`} className="text-[11px] font-semibold text-amber-600 hover:text-amber-700">Override</button>
      )}
      {c.overridden && <button onClick={() => unoverride(c.key)} disabled={busy} className="text-[11px] font-semibold text-slate-400 hover:text-rose-500">Undo</button>}
      {tone === "blocker" && !c.overrideable && <span className="inline-flex items-center gap-0.5 text-[10px] text-rose-500"><ShieldAlert className="w-3 h-3" /> critical</span>}
    </div>
  );

  return (
    <div data-testid="step-panel-go_live">
      <div className={`rounded-2xl p-5 mb-4 ring-1 ${r.ready ? "bg-emerald-50 ring-emerald-200" : "bg-slate-50 ring-slate-200"}`}>
        <div className="flex items-center gap-3">
          <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${r.ready ? "bg-emerald-500 text-white" : "bg-slate-300 text-white"}`}><Rocket className="w-5 h-5" /></span>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-900">{r.ready ? "Ready for Go-Live" : "Not ready for Go-Live"}</h3>
            <p className="text-sm text-slate-500" data-testid="readiness-score">{r.passed_count} / {r.required_count} required checks passed — {r.score}%</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setStatus("ready_for_go_live", "Marked Ready for Go-Live")} disabled={busy || !r.ready} data-testid="mark-ready-btn" className="rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-sm font-semibold px-3 py-2">Mark Ready</button>
            <button onClick={() => setStatus("live", "Company is Live")} disabled={busy || !r.ready} data-testid="mark-live-btn" className="rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-semibold px-3 py-2">Go Live</button>
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-white overflow-hidden"><div className={`h-full ${r.ready ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${r.score}%` }} /></div>
      </div>

      {r.blockers.length > 0 && (
        <Section title={`Blockers — ${r.blockers.length}`} icon={XCircle} tone="text-rose-600" tid="readiness-blockers">
          {r.blockers.map((c) => <Row key={c.key} c={c} tone="blocker" />)}
        </Section>
      )}
      {r.overridden.length > 0 && (
        <Section title={`Overridden — ${r.overridden.length}`} icon={ShieldAlert} tone="text-amber-600" tid="readiness-overridden">
          {r.overridden.map((c) => <Row key={c.key} c={c} tone="blocker" />)}
        </Section>
      )}
      {r.warnings.length > 0 && (
        <Section title={`Warnings — ${r.warnings.length}`} icon={AlertTriangle} tone="text-amber-500" tid="readiness-warnings">
          {r.warnings.map((c) => <Row key={c.key} c={c} tone="warning" />)}
        </Section>
      )}
      <Section title={`Passed — ${r.passed.length}`} icon={CheckCircle2} tone="text-emerald-600" tid="readiness-passed" collapsible open={showPassed} onToggle={() => setShowPassed((v) => !v)}>
        {showPassed && r.passed.map((c) => <Row key={c.key} c={c} />)}
      </Section>
      {r.not_applicable.length > 0 && (
        <Section title={`Not applicable — ${r.not_applicable.length}`} icon={MinusCircle} tone="text-slate-400" tid="readiness-na">
          {r.not_applicable.map((c) => <Row key={c.key} c={c} />)}
        </Section>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, tone, tid, children, collapsible, open, onToggle }) {
  return (
    <div className="mb-3 rounded-xl ring-1 ring-slate-200 bg-white overflow-hidden" data-testid={tid}>
      <button onClick={collapsible ? onToggle : undefined} className={`w-full flex items-center gap-2 px-4 py-2.5 ${collapsible ? "hover:bg-slate-50" : "cursor-default"}`}>
        <Icon className={`w-4 h-4 ${tone}`} />
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {collapsible && <ChevronRight className={`w-4 h-4 text-slate-400 ml-auto transition-transform ${open ? "rotate-90" : ""}`} />}
      </button>
      <div className="px-1 pb-1">{children}</div>
    </div>
  );
}
